import React from 'react';

import mobileIcons from '@/assets/icons/minimized_button/mobile/index.json';
import BotIcon from '@/assets/icons/minimized_button/Bot.svg';
import BubbleIcon from '@/assets/icons/minimized_button/Bubble.svg';
import AiIcon from '@/assets/icons/minimized_button/Ai.svg';
import OneStarIcon from '@/assets/icons/minimized_button/OneStar.svg';
import TwoStarsIcon from '@/assets/icons/minimized_button/TwoStars.svg';

export type SettingsMinimizedButtonLayout = 'floating' | 'sticky' | 'icon';
export type SettingsMinimizedButtonDisplay = 'textIcon' | 'text' | 'icon';
export type SettingsMinimizedButtonPlayAnimation = 'loop' | 'hove' | 'load';

export const CUSTOM_MINIMIZED_BUTTON_ICON = 'custom';
export const NO_MINIMIZED_BUTTON_ICON = 'none';

export const icons = [
  {
    value: 'ai',
    illustration: React.createElement(AiIcon),
    mobile: mobileIcons.Frame420Icon,
  },
  {
    value: 'magic-1',
    illustration: React.createElement(TwoStarsIcon),
    mobile: mobileIcons.TwoStarsIcon,
  },
  {
    value: 'magic-2',
    illustration: React.createElement(OneStarIcon),
    mobile: mobileIcons.OneStarIcon,
  },
  {
    value: 'magic-3',
    illustration: React.createElement(BubbleIcon),
    mobile: mobileIcons.ChatStarIcon,
  },
  {
    value: 'robot',
    illustration: React.createElement(BotIcon),
    mobile: mobileIcons.ChatBotIcon,
  },
];

export const DEFAULT_MINIMIZED_BUTTON_ICON = icons[0].value;

const mapIconsByValue = <T,>(getValue: (icon: (typeof icons)[number]) => T) =>
  icons.reduce((acc, icon) => {
    acc[icon.value] = getValue(icon);
    return acc;
  }, {} as Record<string, T>);

export const iconsDict = mapIconsByValue((icon) => icon.illustration);

export const mobileIconsDict = mapIconsByValue((icon) => icon.mobile);

export const getFallbackMinimizedButtonIcon = () =>
  iconsDict[DEFAULT_MINIMIZED_BUTTON_ICON];

export const getMobileMinimizedButtonIcon = (icon?: string | null) =>
  mobileIconsDict[icon ?? ''] ?? mobileIconsDict[DEFAULT_MINIMIZED_BUTTON_ICON];

const CUSTOM_ICON_HOSTNAME = 'static.wixstatic.com';
const MOBILE_CUSTOM_ICON_SIZE = 20;

const escapeXmlAttribute = (value: string) =>
  value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&apos;');

const getCustomIconUrl = (src?: string | null) => {
  if (!src) {
    return undefined;
  }

  try {
    const url = new URL(src);

    return url.protocol === 'https:' && url.hostname === CUSTOM_ICON_HOSTNAME
      ? url.href
      : undefined;
  } catch {
    return undefined;
  }
};

const SAFE_COLOR_PATTERN = /^(#[0-9a-f]{3,8}|rgba?\([\d\s.,%/]+\))$/i;

const applyIconColor = (svg: string, iconColor?: string | null) => {
  const color = iconColor?.trim();

  return color && SAFE_COLOR_PATTERN.test(color)
    ? svg.replace('fill="currentColor"', `fill="${color}"`)
    : svg;
};

export const getMobileMinimizedButtonSvg = (
  icon?: string | null,
  customIconSrc?: string | null,
  iconColor?: string | null,
) => {
  const customIconUrl =
    icon === CUSTOM_MINIMIZED_BUTTON_ICON
      ? getCustomIconUrl(customIconSrc)
      : undefined;

  if (!customIconUrl) {
    return applyIconColor(getMobileMinimizedButtonIcon(icon), iconColor);
  }

  const href = escapeXmlAttribute(customIconUrl);

  return `<svg xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" width="${MOBILE_CUSTOM_ICON_SIZE}" height="${MOBILE_CUSTOM_ICON_SIZE}" viewBox="0 0 ${MOBILE_CUSTOM_ICON_SIZE} ${MOBILE_CUSTOM_ICON_SIZE}"><image href="${href}" xlink:href="${href}" width="${MOBILE_CUSTOM_ICON_SIZE}" height="${MOBILE_CUSTOM_ICON_SIZE}" preserveAspectRatio="xMidYMid meet"/></svg>`;
};

export const MinimizedButtonIcon = ({
  icon,
  customIconSrc,
  className,
}: {
  icon?: string | null;
  customIconSrc?: string | null;
  className?: string;
}) => {
  if (icon === NO_MINIMIZED_BUTTON_ICON) {
    return null;
  }

  if (icon === CUSTOM_MINIMIZED_BUTTON_ICON && customIconSrc) {
    return React.createElement('img', {
      className,
      src: customIconSrc,
      alt: '',
    });
  }

  return (icon && iconsDict[icon]) || getFallbackMinimizedButtonIcon();
};
